/**
 * Physics simulation for rolling balls on an inclined plane with air resistance
 */

import type {
  Ball,
  SimulationState,
  SimulationParams,
  TrailMarker,
  HistoryPoint,
  Simulation,
  PhysicsConstants
} from '../types';

// Physics constants
const CONSTANTS: PhysicsConstants = {
  PLANE_LENGTH: 28.73,   // meters (length of the inclined plane to achieve 4m height at 8°)
  DRAG_COEFFICIENT: 0.47 // sphere drag coefficient
};

/**
 * Create a ball with computed properties
 */
function createBall(
  id: 'blue' | 'red',
  diameter: number,
  density: number,
  color: string
): Ball {
  const radius = diameter / 2;
  const volume = (4 / 3) * Math.PI * Math.pow(radius, 3);
  const mass = density * volume;
  const crossSectionArea = Math.PI * radius * radius;

  return {
    id,
    diameter,
    density,
    mass,
    radius,
    crossSectionArea,
    position: 0, // starts at top
    velocity: 0, // starts at rest
    color,
    hasLanded: false
  };
}

/**
 * Calculate acceleration for a rolling sphere on an inclined plane with air resistance and rolling resistance
 * Using: a = max(0, (5/7) * g * sin(θ) - μ_rr * g * cos(θ) - (0.5 * C_d * ρ_air * A * v²) / m)
 *
 * The 5/7 factor accounts for rotational inertia of a solid sphere
 * (for a sphere, I = 2/5 * m * r², so a = g*sin(θ)/(1 + 2/5) = 5/7 * g*sin(θ))
 *
 * μ_rr is the rolling resistance coefficient (dimensionless), which opposes motion
 * but never causes backward rolling. If resistance exceeds driving force, ball stops (a = 0).
 */
function calculateAcceleration(
  ball: Ball,
  gravity: number,
  airDensity: number,
  planeAngleDegrees: number,
  rollingResistanceCoeff: number
): number {
  // Convert angle to radians
  const planeAngleRad = planeAngleDegrees * (Math.PI / 180);

  // Gravitational component along the plane, adjusted for rolling motion
  const rollingFactor = 5 / 7; // for a solid sphere
  const gravityComponent = rollingFactor * gravity * Math.sin(planeAngleRad);

  // Rolling resistance component (opposes motion)
  const rollingResistance = rollingResistanceCoeff * gravity * Math.cos(planeAngleRad);

  // Air resistance (drag force)
  const dragForce =
    0.5 *
    CONSTANTS.DRAG_COEFFICIENT *
    airDensity *
    ball.crossSectionArea *
    ball.velocity *
    ball.velocity;

  const dragAcceleration = dragForce / ball.mass;

  // Net acceleration along the plane
  // Important: resistance can never make the ball roll backwards, only stop it
  const netAcceleration = gravityComponent - rollingResistance - dragAcceleration;

  // If ball is moving or acceleration is positive, use calculated value
  // If ball is stopped and forces would push it backward, keep it stopped
  return ball.velocity > 0 || netAcceleration > 0 ? Math.max(0, netAcceleration) : 0;
}

/**
 * Update ball state using Euler integration
 */
function updateBall(
  ball: Ball,
  dt: number,
  gravity: number,
  airDensity: number,
  planeAngle: number,
  rollingResistanceCoeff: number
): void {
  if (ball.hasLanded) return;

  // Calculate acceleration
  const acceleration = calculateAcceleration(ball, gravity, airDensity, planeAngle, rollingResistanceCoeff);

  // Update velocity: v = v + a*dt
  ball.velocity += acceleration * dt;

  // Ensure velocity never goes negative (ball can't roll backwards)
  if (ball.velocity < 0) {
    ball.velocity = 0;
  }

  // Update position: s = s + v*dt
  ball.position += ball.velocity * dt;

  // Check if ball has reached the end of the plane
  if (ball.position >= CONSTANTS.PLANE_LENGTH) {
    ball.position = CONSTANTS.PLANE_LENGTH;
    ball.velocity = 0;
    ball.hasLanded = true;
  }
}

/**
 * Update trail markers
 */
function updateTrail(
  ball: Ball,
  trail: TrailMarker[],
  currentTime: number,
  lastTrailTime: { blue: number; red: number },
  trailInterval: number
): void {
  if (ball.hasLanded) return;

  const timeSinceLastMarker = currentTime - lastTrailTime[ball.id];

  if (timeSinceLastMarker >= trailInterval) {
    trail.push({
      position: ball.position,
      time: currentTime
    });
    lastTrailTime[ball.id] = currentTime;
  }
}

/**
 * Update history for graphs
 */
function updateHistory(
  ball: Ball,
  history: HistoryPoint[],
  currentTime: number
): void {
  history.push({
    time: currentTime,
    position: ball.position,
    velocity: ball.velocity
  });

  // Keep only last 1000 points to avoid memory issues
  if (history.length > 1000) {
    history.shift();
  }
}

/**
 * Main simulation class
 */
export class PhysicsSimulation implements Simulation {
  state: SimulationState;
  private params: SimulationParams;

  constructor(params: SimulationParams) {
    this.params = params;
    this.state = this.createInitialState(params);
  }

  private createInitialState(params: SimulationParams): SimulationState {
    return {
      time: 0,
      running: false,
      blueBall: createBall('blue', params.blueDiameter, params.blueDensity, '#3b82f6'),
      redBall: createBall('red', params.redDiameter, params.redDensity, '#ef4444'),
      blueTrail: [],
      redTrail: [],
      blueHistory: [],
      redHistory: [],
      trailInterval: 0.2, // marker every 0.2 seconds
      lastTrailTime: { blue: 0, red: 0 }
    };
  }

  play(): void {
    this.state.running = true;
  }

  pause(): void {
    this.state.running = false;
  }

  isComplete(): boolean {
    return this.state.blueBall.hasLanded && this.state.redBall.hasLanded;
  }

  step(dt: number): void {
    if (!this.state.running || this.isComplete()) return;

    // Apply time scale
    const scaledDt = dt * this.params.timeScale;

    // Update time
    this.state.time += scaledDt;

    // Update both balls
    updateBall(
      this.state.blueBall,
      scaledDt,
      this.params.gravity,
      this.params.airDensity,
      this.params.planeAngle,
      this.params.frictionCoefficient
    );
    updateBall(
      this.state.redBall,
      scaledDt,
      this.params.gravity,
      this.params.airDensity,
      this.params.planeAngle,
      this.params.frictionCoefficient
    );

    // Update trails
    updateTrail(
      this.state.blueBall,
      this.state.blueTrail,
      this.state.time,
      this.state.lastTrailTime,
      this.state.trailInterval
    );
    updateTrail(
      this.state.redBall,
      this.state.redTrail,
      this.state.time,
      this.state.lastTrailTime,
      this.state.trailInterval
    );

    // Update history for graphs
    updateHistory(this.state.blueBall, this.state.blueHistory, this.state.time);
    updateHistory(this.state.redBall, this.state.redHistory, this.state.time);

    // Stop simulation when both balls have landed
    if (this.isComplete()) {
      this.state.running = false;
    }
  }

  reset(params: SimulationParams): void {
    this.params = params;
    this.state = this.createInitialState(params);
  }
}

export { CONSTANTS };
